import { Check } from "lucide-react";
import { cn } from "@/lib/utils/helpers";
import {
    entityEditTabBarWrapClassName,
    entityEditTabButtonClassName,
    entityEditTabListClassName,
    entityEditTabListEqualClassName,
    type EntityEditTabAlign,
    type EntityEditTabLayout,
} from "@/components/ui/entityEditTabNavStyles";

export interface EntityEditTabItem {
    id: string;
    label: string;
    /** e.g. `company-edit-tab-companyinfo` / `property-edit-tab-propertyinfo` */
    testId: string;
    /** When set, show a green check before the label (e.g. transfer wizard step complete). */
    complete?: boolean;
    /** When set, tab is not navigable. */
    disabled?: boolean;
}

export interface EntityEditTabBarProps {
    /** Visible tabs in order (callers filter by policy before passing). */
    tabs: EntityEditTabItem[];
    activeTab: string;
    onSelect: (id: string) => void;
    /** Sets `data-testid` on the `nav` (tablist). */
    navTestId?: string;
    ariaLabel?: string;
    /** Merged with the default wrap (e.g. `overflow-x-auto` for long strips). */
    wrapClassName?: string;
    /**
     * `distribute` (default): equal-width tabs on large screens — used by all modules.
     * `start`: content-sized, left-aligned tabs — Application detail only when underwrite checklist is open.
     * Takes precedence over `layout` when set to `start`.
     */
    align?: EntityEditTabAlign;
    /**
     * `spread` (default): existing behaviour, driven by `align`.
     * `equal`: equal-width tabs at all breakpoints (Property → Config sub-tabs).
     */
    layout?: EntityEditTabLayout;
}

/**
 * Shared tab strip for Company / Property (and other) entity edit pages — spacing, full-width `lg` layout, and active “nook” come from `entityEditTabNavStyles`.
 */
export function EntityEditTabBar({
    tabs,
    activeTab,
    onSelect,
    navTestId,
    ariaLabel = "Tabs",
    wrapClassName,
    align = "distribute",
    layout = "spread",
}: EntityEditTabBarProps) {
    const equal = align !== "start" && layout === "equal";
    return (
        <div
            className={cn(
                entityEditTabBarWrapClassName,
                "min-w-0 overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch]",
                wrapClassName,
            )}
        >
            <nav
                className={equal ? entityEditTabListEqualClassName : entityEditTabListClassName(align)}
                aria-label={ariaLabel}
                role="tablist"
                data-testid={navTestId}
            >
                {tabs.map((tab) => {
                    const disabled = tab.disabled === true;
                    return (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        data-testid={tab.testId}
                        disabled={disabled}
                        aria-disabled={disabled}
                        aria-selected={activeTab === tab.id}
                        onClick={() => {
                            if (disabled) return;
                            onSelect(tab.id);
                        }}
                        className={entityEditTabButtonClassName(activeTab === tab.id, {
                            disabled,
                            align,
                            layout,
                        })}
                    >
                        {tab.complete ? (
                            <Check className="h-4 w-4 shrink-0 text-[#78bf0a] stroke-[2.5]" aria-hidden />
                        ) : null}
                        <span className={equal ? "min-w-0 truncate" : undefined}>{tab.label}</span>
                    </button>
                );
                })}
            </nav>
        </div>
    );
}
